import React, { useState, useEffect, useCallback, useRef } from 'react';
import IdeaPopup from './IdeaPopup';
import axios from "../utils/axiosInstance";
import learningLinks from '../data/learningLinks';
import { useNavigate } from 'react-router-dom';
import { useIdea } from '../context/IdeaContext';
import { useAuth } from '../context/AuthContext';
import {
  Download, CheckCircle, AlertCircle, Info, X,
  Pencil, Check, ChevronDown, Lock, Unlock,
  Sparkles, ArrowRight, Lightbulb, RefreshCw,
  Zap, BookOpen
} from "lucide-react";
import buildMDIHPdfName from "../utils/pdfNaming";

/* ─────────────────────────────────────────
   Toast
───────────────────────────────────────── */
const Toast = ({ toasts, removeToast }) => {
  const cfg = {
    success: { icon: <CheckCircle size={15} />, bar: 'bg-green-500',  wrap: 'bg-white border-green-200',      text: 'text-slate-700'  },
    error:   { icon: <AlertCircle  size={15} />, bar: 'bg-red-500',    wrap: 'bg-white border-red-200',        text: 'text-slate-700'  },
    warning: { icon: <AlertCircle  size={15} />, bar: 'bg-amber-400',  wrap: 'bg-white border-amber-200',      text: 'text-slate-700'  },
    mentor:  { icon: <Sparkles     size={15} />, bar: 'bg-purple-500', wrap: 'bg-purple-50 border-purple-200', text: 'text-purple-900' },
    info:    { icon: <Info         size={15} />, bar: 'bg-blue-500',   wrap: 'bg-white border-blue-200',       text: 'text-slate-700'  },
  };
  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-2 w-80 pointer-events-none">
      {toasts.map(t => {
        const c = cfg[t.type] || cfg.info;
        return (
          <div key={t.id} className={`flex items-start gap-3 border rounded-xl px-4 py-3 shadow-lg pointer-events-auto overflow-hidden relative ${c.wrap}`}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.bar}`} />
            <span className="shrink-0 mt-0.5 ml-1">{c.icon}</span>
            <p className={`text-sm flex-1 leading-snug ${c.text}`}>{t.msg}</p>
            <button onClick={() => removeToast(t.id)} className="shrink-0 text-slate-300 hover:text-slate-500 mt-0.5">
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────
   Mentor Bubble
───────────────────────────────────────── */
const MentorBubble = ({ message }) => (
  <div className="flex gap-3 items-start">
    <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-sm font-bold shadow-md">
      AI
    </div>
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 rounded-2xl rounded-tl-none px-4 py-3 flex-1 shadow-sm">
      <p className="text-xs font-semibold text-purple-600 mb-1">Your AI Mentor</p>
      <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   Step Tracker
───────────────────────────────────────── */
const StepTracker = ({ step }) => {
  const steps = [
    { n: 1, label: 'Define'   },
    { n: 2, label: 'Generate' },
    { n: 3, label: 'Select'   },
    { n: 4, label: 'Validate' },
  ];
  return (
    <div className="flex items-center">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              step > s.n   ? 'bg-green-500 text-white shadow'
              : step === s.n ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-200'
              :                'bg-slate-200 text-slate-400'
            }`}>
              {step > s.n ? <CheckCircle size={14} /> : s.n}
            </div>
            <span className={`text-[10px] font-medium whitespace-nowrap ${step >= s.n ? 'text-blue-700' : 'text-slate-400'}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 flex-1 mx-1 mb-4 transition-all duration-500 ${step > s.n ? 'bg-green-400' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────
   Skeleton
───────────────────────────────────────── */
const IdeaSkeleton = () => (
  <div className="border border-slate-200 rounded-2xl p-6 space-y-4 animate-pulse bg-white">
    <div className="h-6 bg-slate-200 rounded w-2/3" />
    <div className="space-y-2">
      <div className="h-3.5 bg-slate-100 rounded w-full" />
      <div className="h-3.5 bg-slate-100 rounded w-5/6" />
      <div className="h-3.5 bg-slate-100 rounded w-4/6" />
    </div>
    <div className="h-10 bg-purple-100 rounded-xl" />
    <div className="flex gap-2">
      {[1,2,3].map(i => <div key={i} className="h-8 bg-slate-100 rounded-lg w-28" />)}
    </div>
  </div>
);

/* ─────────────────────────────────────────
   Editable Field
───────────────────────────────────────── */
const EditableField = ({ value, onChange, multiline = false }) => {
  const [editing, setEditing] = useState(false);
  if (editing) {
    const p = {
      value,
      onChange: e => onChange(e.target.value),
      autoFocus: true,
      className: 'w-full border-2 border-blue-400 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-blue-50',
    };
    return (
      <div className="relative">
        {multiline ? <textarea rows={4} {...p} /> : <input type="text" {...p} />}
        <button onClick={() => setEditing(false)} className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-0.5 hover:bg-green-600">
          <Check size={12} />
        </button>
      </div>
    );
  }
  return (
    <div className="group relative cursor-text" onClick={() => setEditing(true)}>
      {multiline
        ? <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap pr-5">{value}</p>
        : <h3 className="text-xl font-bold text-blue-900 pr-5">{value}</h3>
      }
      <span className="absolute top-0 right-0 opacity-0 group-hover:opacity-60 transition text-slate-400">
        <Pencil size={12} />
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────
   Recent Ideas Dropdown
───────────────────────────────────────── */
const RecentIdeasDropdown = ({ ideas, onSelect }) => {
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handlePick = idea => {
    setSelected(idea);
    setOpen(false);
    onSelect(idea);
  };

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between bg-white border border-slate-200 hover:border-blue-400 rounded-xl px-4 py-3 text-sm text-slate-700 transition shadow-sm"
      >
        <span className="flex items-center gap-2">
          <RefreshCw size={13} className="text-blue-400 shrink-0" />
          {selected
            ? <span className="font-medium text-blue-800 truncate">{selected.ideaTitle}</span>
            : <span className="text-slate-400">Load a previous saved idea…</span>
          }
        </span>
        <ChevronDown size={15} className={`text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-4 pt-3 pb-1.5 border-b border-slate-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Your last 5 saved ideas</p>
          </div>
          {ideas.map((idea, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handlePick(idea)}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 transition border-b border-slate-50 last:border-0 group"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-blue-800 truncate">{idea.ideaTitle}</span>
                <span className="text-[10px] text-blue-400 opacity-0 group-hover:opacity-100 font-semibold shrink-0">Load →</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 truncate">{idea.problem}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═════════════════════════════════════════
   MAIN COMPONENT
═════════════════════════════════════════ */
const IdeaGeneration = () => {
  const [formData, setFormData]         = useState({ industry: '', problem: '', targetUser: '' });
  const [formErrors, setFormErrors]     = useState({});
  const [ideas, setIdeas]               = useState([]);
  const [recentIdeas, setRecentIdeas]   = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [recordId, setRecordId]         = useState(null);
  const [activeIdea, setActiveIdea]     = useState(null);
  const [popupType, setPopupType]       = useState(null);
  const [showPopup, setShowPopup]       = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [pdfLoading, setPdfLoading]     = useState(false);
  const [ideaConfirmed, setIdeaConfirmed] = useState(false);
  const [toasts, setToasts]             = useState([]);
  const [hasViewedLearning, setHasViewedLearning] = useState(
    () => localStorage.getItem('learned-idea-generation') === 'true'
  );

  const { selectedIdea, setSelectedIdea } = useIdea();
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const isAuthenticated = !!user?._id;

  /* current wizard step */
  const currentStep =
    ideaConfirmed ? 4 :
    ideas.length  ? 3 :
    (formData.industry && formData.problem && formData.targetUser) ? 2 : 1;

  /* toasts */
  const showToast = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 6000);
  }, []);
  const removeToast = useCallback(id => setToasts(p => p.filter(t => t.id !== id)), []);

  /* form */
  const handleChange = e => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    if (formErrors[e.target.name]) setFormErrors(p => ({ ...p, [e.target.name]: '' }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.industry.trim())   err.industry   = 'Required';
    if (!formData.problem.trim())    err.problem    = 'Required';
    if (!formData.targetUser.trim()) err.targetUser = 'Required';
    setFormErrors(err);
    return !Object.keys(err).length;
  };

  /* fetch */
  const fetchRecentIdeas = async () => {
    try {
      const res = await axios.get('/api/idea-generation/recent');
      setRecentIdeas(res.data.ideas || []);
    } catch (e) { console.error(e); }
  };

  const fetchCallToAction = async () => {
    try {
      const res = await axios.post('/api/call-to-action', {
        eventType: 'idea-generated',
        context: `${formData.industry}, ${formData.problem}, ${formData.targetUser}`,
      });
      setPopupMessage(res.data.message);
      setTimeout(() => setPopupMessage(null), 12000);
    } catch (e) { console.error(e); }
  };

  /* generate */
  const handleGenerateIdeas = async () => {
    if (!isAuthenticated) {
      showToast('Please log in to start generating ideas.', 'warning');
      navigate('/login');
      return;
    }
    if (!validateForm()) {
      showToast('Complete all three fields — your mentor needs this context!', 'warning');
      return;
    }
    try {
      setLoading(true);
      setIdeas([]);
      setIdeaConfirmed(false);
      const res = await axios.post('/api/idea-generation', { ...formData });
      setIdeas(res.data.ideas || []);
      setRecordId(res.data.recordId || null);
      fetchCallToAction();
      await axios.post('/api/progress', { step: 'ideaGenerated', value: true });
      showToast("Idea ready! Review it below — click 'Select & Continue' when it feels right.", 'mentor');
    } catch (err) {
      console.error(err);
      showToast('Generation failed — please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* regenerate */
  const handleRegenerate = async () => {
    if (!validateForm()) return;
    try {
      setRegenerating(true);
      const exclude = ideas.map(i => `${i.title} ${i.description}`);
      const res = await axios.post('/api/idea-generation', { ...formData, exclude });
      setIdeas(res.data.ideas || []);
      setRecordId(res.data.recordId);
      setIdeaConfirmed(false);
      showToast("Fresh idea! Pick the one that genuinely excites you — that's your signal.", 'mentor');
    } catch (err) {
      console.error(err);
      showToast('Regeneration failed. Try again.', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  /* load from dropdown */
  const handleLoadRecentIdea = idea => {
    setSelectedIdea({
      ideaTitle:   idea.ideaTitle,
      description: idea.description,
      industry:    idea.industry,
      problem:     idea.problem,
      targetUser:  idea.targetUser,
      recordId:    idea.recordId,
    });
    showToast(`Loaded "${idea.ideaTitle}" — hit Select & Continue to lock it in.`, 'info');
  };

  /* select idea → unlock market research */
  const handleSelectIdea = async idea => {
    const finalRecordId = idea.recordId || recordId;
    try {
      await axios.post('/api/idea-generation/save', {
        data: {
          title:       idea.title,
          description: idea.description,
          industry:    formData.industry,
          problem:     formData.problem,
          solution:    idea.description,
          targetUser:  formData.targetUser,
        },
      });
      setSelectedIdea({
        ideaId:       idea.ideaId,
        recordId:     finalRecordId,
        ideaTitle:    idea.title,
        problem:      formData.problem,
        targetUser:   formData.targetUser,
        industry:     formData.industry,
        solution:     idea.description,
        coreFeatures: idea.coreFeatures || ['Feature 1', 'Feature 2'],
      });
      setIdeaConfirmed(true);
      fetchRecentIdeas();
      showToast(`"${idea.title}" locked in! Your mentor is ready to guide your market research.`, 'mentor');
    } catch (err) {
      console.error(err);
      showToast('Failed to save idea. Try again.', 'error');
    }
  };

  const updateIdeaField = (index, field, value) => {
    setIdeas(prev => {
      const u = [...prev];
      u[index] = { ...u[index], [field]: value };
      return u;
    });
  };

  /* pdf */
  const startupName = selectedIdea?.ideaTitle || selectedIdea?.title || 'My Startup Idea';

  const handleDownloadPDF = async () => {
    if (pdfLoading) return;
    try {
      setPdfLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/pdf/idea-generation',
        { ideaId: selectedIdea?.ideaId },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildMDIHPdfName(startupName, 'Idea_Report');
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('PDF downloaded!', 'success');
    } catch (err) {
      console.error(err);
      showToast('PDF download failed.', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  /* learning */
  const handleLearningClick = () => {
    setHasViewedLearning(true);
    localStorage.setItem('learned-idea-generation', 'true');
    navigate(`/learning-mentorship?video=${learningLinks['idea-generation'].videoId}`);
  };

  /* effects */
  useEffect(() => {
    if (isAuthenticated) fetchRecentIdeas();
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedIdea && (selectedIdea.ideaTitle || selectedIdea.problem)) {
      setFormData({
        industry:   selectedIdea.industry   || '',
        problem:    selectedIdea.problem    || '',
        targetUser: selectedIdea.targetUser || '',
      });
      setIdeas([{
        title:       selectedIdea.ideaTitle    || selectedIdea.title || 'My Startup Idea',
        description: selectedIdea.solution     || selectedIdea.description || '',
        ideaId:      selectedIdea.ideaId       || selectedIdea.recordId || 'restored',
        recordId:    selectedIdea.recordId,
        aiNote:      'Restored from your last session 🧠',
      }]);
    }
  }, [selectedIdea]);

  /* field config */
  const fields = [
    { name: 'industry',   label: 'Industry',   placeholder: 'e.g. AgriTech, HealthTech, EdTech'         },
    { name: 'problem',    label: 'Problem',     placeholder: 'e.g. Farmers lack affordable picking tools' },
    { name: 'targetUser', label: 'Target User', placeholder: 'e.g. Smallholder farmers in SE Asia'        },
  ];

  /* mentor messages per step */
  const mentorMessage = {
    1: "Welcome! Every great startup begins with a real problem. Describe the industry, what's broken, and who suffers from it — the more specific, the better your idea will be.",
    2: "Great context! Hit Generate and I'll craft a focused concept for you. Don't worry about perfection — you can regenerate as many times as you need.",
    3: "Here's your idea! Read it carefully. You can click any field to refine the title or description. When it resonates with you, hit Select & Continue to lock it in.",
    4: "Excellent! Your idea is confirmed. Now it's time to stress-test it against the real market — click below to begin your 5P Market Research.",
  }[currentStep];

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">

        {/* ── Header ── */}
        <div className="pb-5 border-b border-slate-200 space-y-4">
          <div>
            <h1 className="text-3xl font-bold text-blue-900 tracking-tight">
              Idea Generation <span className="text-blue-500">Workspace</span>
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Your AI mentor guides you step-by-step — from raw problem to validated startup concept.
            </p>
          </div>
          <StepTracker step={currentStep} />
        </div>

        {/* ── Mentor bubble ── */}
        <MentorBubble message={mentorMessage} />

        {/* ── AI mentor popup (call-to-action overlay) ── */}
        {popupMessage && (
          <div className="fixed bottom-24 right-6 bg-white border border-purple-200 shadow-xl rounded-2xl p-4 z-50 w-80">
            <div className="flex gap-3 items-start">
              <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-xs font-bold">AI</div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-purple-600 mb-1">Mentor Insight</p>
                <p className="text-sm text-slate-600 leading-relaxed">{popupMessage}</p>
              </div>
              <button onClick={() => setPopupMessage(null)} className="text-slate-300 hover:text-slate-500"><X size={14} /></button>
            </div>
          </div>
        )}

        {/* ═══ CARD 1: Define the Problem ═══ */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
            <h2 className="text-base font-bold text-blue-900">Define the Problem</h2>
          </div>

          {/* ── Recent ideas dropdown ── */}
          {recentIdeas.length > 0 && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Continue with a saved idea
              </label>
              <RecentIdeasDropdown ideas={recentIdeas} onSelect={handleLoadRecentIdea} />
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 whitespace-nowrap">or describe a new one</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          )}

          {/* ── Form fields ── */}
          <div className="grid sm:grid-cols-3 gap-4">
            {fields.map(({ name, label, placeholder }) => (
              <div key={name} className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
                <input
                  type="text"
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm text-slate-700 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none transition ${
                    formErrors[name] ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                />
                {formErrors[name] && (
                  <p className="text-[11px] text-red-500 font-medium">{formErrors[name]}</p>
                )}
              </div>
            ))}
          </div>

          {/* ── Buttons ── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleGenerateIdeas}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-bold text-sm transition shadow-sm"
            >
              {loading ? (
                <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Generating…</>
              ) : (
                <><Zap size={15} /> Generate Idea</>
              )}
            </button>

            {ideas.length > 0 && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                title="Get a different idea using the same inputs"
                className="flex items-center justify-center gap-2 border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-3 rounded-xl font-bold text-sm transition"
              >
                {regenerating ? (
                  <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Regenerating…</>
                ) : (
                  <><RefreshCw size={14} /> Regenerate</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ═══ CARD 2: Review Idea ═══ */}
        {(loading || ideas.length > 0) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
              <h2 className="text-base font-bold text-blue-900">Review &amp; Refine Your Idea</h2>
            </div>

            {loading ? (
              <IdeaSkeleton />
            ) : (
              ideas.map((idea, index) => (
                <div
                  key={idea.ideaId || index}
                  className={`bg-white border rounded-2xl p-6 space-y-4 shadow-sm transition-all duration-300 ${
                    ideaConfirmed ? 'border-green-400 ring-2 ring-green-100' : 'border-slate-200'
                  }`}
                >
                  {ideaConfirmed && (
                    <div className="flex items-center gap-2 text-green-700 text-xs font-bold bg-green-50 rounded-lg px-3 py-2">
                      <CheckCircle size={14} /> Idea confirmed — ready for market research
                    </div>
                  )}

                  <EditableField value={idea.title} onChange={val => updateIdeaField(index, 'title', val)} />
                  <EditableField value={idea.description} onChange={val => updateIdeaField(index, 'description', val)} multiline />

                  {idea.aiNote && (
                    <div className="flex items-start gap-2 bg-blue-50 border-l-4 border-blue-400 rounded-r-xl px-3 py-2.5">
                      <Lightbulb size={13} className="text-blue-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-blue-700 italic">{idea.aiNote}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button
                      onClick={() => { setActiveIdea(idea); setPopupType('feedback'); setShowPopup(true); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 transition"
                    >
                      🎓 Mentor Insight
                    </button>
                    <button
                      onClick={() => { setActiveIdea(idea); setPopupType('improve'); setShowPopup(true); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 transition"
                    >
                      ✨ Improve Concept
                    </button>
                    <button
                      onClick={() => { setActiveIdea(idea); setPopupType('potential'); setShowPopup(true); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-green-200 text-green-700 hover:bg-green-50 transition"
                    >
                      📈 Market Potential
                    </button>

                    {!ideaConfirmed ? (
                      <button
                        onClick={() => handleSelectIdea(idea)}
                        className="ml-auto text-xs font-bold px-4 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition shadow-sm"
                      >
                        ✅ Select &amp; Continue →
                      </button>
                    ) : (
                      <span className="ml-auto text-xs font-bold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle size={12} /> Selected
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ═══ CARD 3: Market Research CTA ═══ */}
        {ideas.length > 0 && !loading && (
          <div className={`rounded-2xl p-6 space-y-4 border transition-all duration-500 ${
            ideaConfirmed
              ? 'bg-gradient-to-br from-green-50 to-blue-50 border-green-300 shadow-md'
              : 'bg-slate-100 border-slate-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                ideaConfirmed ? 'bg-green-500 shadow-md' : 'bg-slate-300'
              }`}>
                {ideaConfirmed
                  ? <Unlock size={18} className="text-white" />
                  : <Lock   size={18} className="text-slate-500" />
                }
              </div>
              <div>
                <h3 className={`font-bold text-base transition-all ${ideaConfirmed ? 'text-blue-900' : 'text-slate-400'}`}>
                  {ideaConfirmed ? '🚀 Step 3 — Deep Market Validation' : 'Step 3 unlocks after you select an idea'}
                </h3>
                <p className={`text-sm mt-0.5 ${ideaConfirmed ? 'text-slate-500' : 'text-slate-400'}`}>
                  {ideaConfirmed
                    ? 'Validate your concept with the full 5P Market Research Suite.'
                    : 'Hit "Select & Continue" on your idea above to unlock this step.'
                  }
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/market-research')}
              disabled={!ideaConfirmed}
              title={!ideaConfirmed ? 'Select an idea first to unlock this' : ''}
              className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                ideaConfirmed
                  ? 'bg-blue-700 hover:bg-blue-800 text-white shadow-md cursor-pointer'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {ideaConfirmed
                ? <><span>Go To Market Research Suite</span><ArrowRight size={16} /></>
                : <><Lock size={14} /><span>Locked — select an idea first</span></>
              }
            </button>

            {ideaConfirmed && (
              <button
                onClick={handleDownloadPDF}
                disabled={pdfLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-60 transition"
              >
                {pdfLoading
                  ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Preparing PDF…</>
                  : <><Download size={15} /> Download Idea Report</>
                }
              </button>
            )}
          </div>
        )}

        {/* ── Learning CTA ── */}
        {learningLinks?.['idea-generation'] && (
          <div className="pt-2 pb-6 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-slate-400">Want to sharpen your idea generation skills?</p>
            <button
              onClick={handleLearningClick}
              className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
            >
              <BookOpen size={14} />
              {hasViewedLearning ? 'Revisit Learning Resources' : '🎓 Learn Idea Generation Techniques'}
            </button>
          </div>
        )}
      </div>

      {/* ── Idea Popup ── */}
      {showPopup && activeIdea && (
        <IdeaPopup
          idea={activeIdea}
          type={popupType}
          onClose={() => setShowPopup(false)}
          onSaveImprovement={updated => {
            setActiveIdea(updated);
            setIdeas(prev => prev.map(i => i.title === updated.title ? updated : i));
          }}
        />
      )}

      {/* ── Toasts ── */}
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default IdeaGeneration;
